import axios from 'axios'
import apiService from '@/services/api'

/** Extract a user-facing message from an API error (Axios or backend payload). */
export function getContractsErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.response?.data) {
      const data = err.response.data as { message?: string; error?: { message?: string } }
      const msg = data?.error?.message ?? data?.message
      if (typeof msg === 'string' && msg) return msg
    }
    // No response = request never reached server (backend down, wrong URL, CORS, or network)
    if (!err.response && (err.message === 'Network Error' || err.code === 'ERR_NETWORK')) {
      const base = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'
      return `Cannot reach the API at ${base}. Check that the backend is running (e.g. \`npm run dev\` in kay-curzi-backend) and CORS allows this origin.`
    }
  }
  return err instanceof Error ? err.message : 'Failed to fetch contracts'
}

export interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
  meta?: PaginationMeta
}

export interface PaginationMeta {
  page: number
  limit: number
  total: number
  totalPages: number
}

export interface ContractVendor {
  id: string
  name: string
  [key: string]: unknown
}

export interface Contract {
  id: string
  vendorId: string
  contractNumber: string
  title: string
  type: 'buy' | 'sale'
  fileUrl: string | null
  productSpecifications: string | null
  pricingSheet: string | null
  startDate: string | null
  endDate: string | null
  sku?: string | null
  quantity?: string | null
  price?: string | null
  priceCurrency?: string | null
  deliveryConditions?: string | null
  deliveryPeriod?: string | null
  incoterm?: string | null
  customer?: string | null
  forwarder?: string | null
  pol?: string | null
  pod?: string | null
  booking?: string | null
  factory?: string | null
  paymentTerms?: string | null
  createdAt: string
  updatedAt: string
  vendor?: ContractVendor | null
}

export interface ContractsListResponse {
  contracts: Contract[]
  meta: PaginationMeta
}

export interface CreateContractPayload {
  vendorId: string
  contractNumber: string
  title: string
  type: 'buy' | 'sale'
  productSpecifications?: string
  pricingSheet?: string
  startDate?: string
  endDate?: string
  sku?: string
  quantity?: string
  price?: string
  priceCurrency?: string
  deliveryConditions?: string
  deliveryPeriod?: string
  incoterm?: string
  customer?: string
  forwarder?: string
  pol?: string
  pod?: string
  booking?: string
  factory?: string
  paymentTerms?: string
}

export interface UpdateContractPayload {
  vendorId?: string
  contractNumber?: string
  title?: string
  type?: 'buy' | 'sale'
  productSpecifications?: string
  pricingSheet?: string
  startDate?: string
  endDate?: string
  sku?: string
  quantity?: string
  price?: string
  priceCurrency?: string
  deliveryConditions?: string
  deliveryPeriod?: string
  incoterm?: string
  customer?: string
  forwarder?: string
  pol?: string
  pod?: string
  booking?: string
  factory?: string
  paymentTerms?: string
  fileUrl?: string | null
}

export async function getContracts(params: {
  page?: number
  limit?: number
  search?: string
  type?: 'buy' | 'sale'
  supplier?: string // vendorId
}): Promise<ContractsListResponse> {
  const searchParams = new URLSearchParams()
  if (params.page != null) searchParams.set('page', String(params.page))
  if (params.limit != null) searchParams.set('limit', String(params.limit))
  if (params.search) searchParams.set('search', params.search)
  if (params.type) searchParams.set('type', params.type)
  if (params.supplier) searchParams.set('supplier', params.supplier)
  const query = searchParams.toString()
  const url = query ? `/contracts?${query}` : '/contracts'
  const response = await apiService.get<ApiResponse<ContractsListResponse>>(url)
  if (!response.success) throw new Error(response.message || 'Failed to fetch contracts')
  return response.data
}

export async function getContractById(id: string): Promise<Contract> {
  const response = await apiService.get<ApiResponse<Contract>>(`/contracts/${id}`)
  if (!response.success) throw new Error(response.message || 'Failed to fetch contract')
  return response.data
}

export async function createContract(payload: CreateContractPayload): Promise<Contract> {
  const response = await apiService.post<ApiResponse<Contract>>('/contracts', payload)
  if (!response.success) throw new Error(response.message || 'Failed to create contract')
  return response.data
}

export async function updateContract(id: string, payload: UpdateContractPayload): Promise<Contract> {
  const response = await apiService.put<ApiResponse<Contract>>(`/contracts/${id}`, payload)
  if (!response.success) throw new Error(response.message || 'Failed to update contract')
  return response.data
}

export async function deleteContract(id: string): Promise<void> {
  const response = await apiService.delete<ApiResponse<null>>(`/contracts/${id}`)
  if (!response.success) throw new Error(response.message || 'Failed to delete contract')
}

export async function uploadContractFile(contractId: string, file: File): Promise<Contract> {
  const formData = new FormData()
  formData.append('file', file)
  const response = await apiService.instance.post<ApiResponse<Contract>>(
    `/contracts/${contractId}/upload-file`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  )
  const data = response.data
  if (!data.success) throw new Error(data.message || 'Failed to upload file')
  return data.data
}

const contractFileRequestConfig = { suppressGlobalError: true as const }

/** Get a viewable URL for the contract's uploaded file (opens in new tab). */
export async function getContractFileViewUrl(contractId: string): Promise<string> {
  const response = await apiService.get<ApiResponse<{ url: string }>>(
    `/contracts/${contractId}/view-file`,
    contractFileRequestConfig
  )
  if (!response.success) throw new Error(response.message || 'Failed to get file URL')
  return response.data.url
}

/** Download the contract's uploaded file using a signed view URL. */
export async function downloadContractFile(contractId: string, filename: string): Promise<void> {
  const url = await getContractFileViewUrl(contractId)
  const link = document.createElement('a')
  link.href = url
  link.download = filename || 'contract'
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export interface ContractSummary {
  total: number
  totalTrend: number
  completed: number
  completedTrend: number
  inProgress: number
  inProgressTrend: number
  pending: number
  pendingTrend: number
}

export interface ContractCompletionItem {
  name: string
  value: number
  percentage: number
  color: string
}

export interface ContractedDataItem {
  month: string
  totalContract: number
  delivered: number
  contractCount: number
  deliveredCount: number
}

export interface MonthlyPerformanceItem {
  month: string
  profit: number
  loss: number
}

export interface PerformanceTotals {
  totalProfit: number
  totalLoss: number
  netProfit: number
}

export interface ContractPerformance {
  summary: ContractSummary
  contractCompletion: ContractCompletionItem[]
  contractedData: ContractedDataItem[]
  monthlyPerformanceData: MonthlyPerformanceItem[]
  totals: PerformanceTotals
  year: number
}

export async function getContractPerformance(params?: {
  year?: number
  type?: 'buy' | 'sale'
}): Promise<ContractPerformance> {
  const searchParams = new URLSearchParams()
  if (params?.year) searchParams.set('year', String(params.year))
  if (params?.type) searchParams.set('type', params.type)
  const query = searchParams.toString()
  const url = query ? `/contracts/performance?${query}` : '/contracts/performance'
  const response = await apiService.get<ApiResponse<ContractPerformance>>(url)
  if (!response.success) throw new Error(response.message || 'Failed to fetch performance')
  return response.data
}
